import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { ArrowLeft, CheckCircle, PlayCircle, Loader, ChevronDown, ChevronUp, AlertCircle, GitBranch } from 'lucide-react';
import { getPlan, updatePlan, evaluatePlan, getAllQueries } from '../services/api';
import { formatLocalDate } from '../utils/timezone';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { HtmlRenderer } from '../components/shared/HtmlRenderer';
import { Card } from '../components/ui/Card';
import { stripFrontendCodeOuterCard } from '../utils/stripFrontendCodeOuterCard';

const TIME_FRAME_LABEL_KEYS = {
    'yesterday': 'timeFrameLabels.yesterday',
    'week_to_date': 'timeFrameLabels.weekToDate',
    'last_week': 'timeFrameLabels.lastWeek',
    'month_to_date': 'timeFrameLabels.monthToDate',
    'last_month': 'timeFrameLabels.lastMonth',
    'custom': 'timeFrameLabels.custom',
};

const PRIORITY_COLORS = {
    'HIGH': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-200' },
    'MEDIUM': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-200' },
    'LOW': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-200' },
};

/** Shared Card header/title chrome — embedded HTML blocks + right-column sections */
const PLAN_DETAIL_EMBEDDED_CARD_HEADER_CLASS = 'border-gray-200 px-6 py-4';
const PLAN_DETAIL_EMBEDDED_CARD_TITLE_CLASS = 'text-sm font-semibold text-gray-500';

/** Card shell with shared plan-detail header/title chrome (charts, recommendation iframe, sidebar sections). */
function PlanDetailSectionCard({ title, padding, className = '', children }) {
    return (
        <Card
            padding={padding}
            className={className}
            title={title}
            headerClassName={PLAN_DETAIL_EMBEDDED_CARD_HEADER_CLASS}
            titleClassName={PLAN_DETAIL_EMBEDDED_CARD_TITLE_CLASS}
        >
            {children}
        </Card>
    );
}

export const PlanDetailPage = () => {
    const { t } = useTranslation(['page-plan-detail', 'common']);
    const params = useParams();
    const databaseId = params.databaseId;
    const planIdFromPath = params.planId;
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const planId = searchParams.get('planId') || planIdFromPath;
    const reportId = searchParams.get('reportId');
    const timeFrameType = searchParams.get('timeFrameType');
    
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [evaluating, setEvaluating] = useState(false);
    const [completedSteps, setCompletedSteps] = useState([]);
    const [showEvaluationHistory, setShowEvaluationHistory] = useState(false);
    const [databaseQueriesMap, setDatabaseQueriesMap] = useState({});

    const fetchPlan = useCallback(async () => {
        if (!planId) return;
        setLoading(true);
        try {
            const response = await getPlan(planId);
            const planData = response.data?.data;
            setPlan(planData);
            if (planData?.completed_steps) {
                setCompletedSteps(planData.completed_steps);
            }
        } catch (error) {
            console.error('Failed to fetch plan:', error);
        } finally {
            setLoading(false);
        }
    }, [planId]);

    useEffect(() => {
        fetchPlan();
    }, [fetchPlan]);

    useEffect(() => {
        const db = databaseId || plan?.database_connection_id;
        if (!db) return;
        getAllQueries(db)
            .then((response) => {
                const queries = response.data?.data?.queries || [];
                const qMap = {};
                queries.forEach((q) => {
                    qMap[q.id] = q;
                });
                setDatabaseQueriesMap(qMap);
            })
            .catch((error) => {
                console.error('Failed to fetch queries:', error);
                setDatabaseQueriesMap({});
            });
    }, [databaseId, plan?.database_connection_id]);

    const openContinuedPlan = () => {
        if (!plan?.continued_by_plan_id) return;
        const dbId = databaseId || plan.database_connection_id;
        const rep = reportId || (plan.report_generation_id != null ? String(plan.report_generation_id) : '');
        const tf = timeFrameType || plan.time_frame_type || '';
        const qs = new URLSearchParams({ planId: String(plan.continued_by_plan_id) });
        if (rep) qs.set('reportId', rep);
        if (tf) qs.set('timeFrameType', tf);
        if (dbId) {
            navigate(`/reports/${dbId}/plan?${qs.toString()}`);
        } else {
            navigate(`/plans/${plan.continued_by_plan_id}`);
        }
    };

    const openPriorPlan = () => {
        if (!plan?.supersedes_plan_id) return;
        const dbId = databaseId || plan.database_connection_id;
        const rep = reportId || (plan.report_generation_id != null ? String(plan.report_generation_id) : '');
        const tf = timeFrameType || plan.time_frame_type || '';
        const qs = new URLSearchParams({ planId: String(plan.supersedes_plan_id) });
        if (rep) qs.set('reportId', rep);
        if (tf) qs.set('timeFrameType', tf);
        if (dbId) {
            navigate(`/reports/${dbId}/plan?${qs.toString()}`);
        } else {
            navigate(`/plans/${plan.supersedes_plan_id}`);
        }
    };

    const toggleStep = async (stepIdx) => {
        if (plan?.status === 'superseded') return;
        const newCompletedSteps = completedSteps.includes(stepIdx)
            ? completedSteps.filter(i => i !== stepIdx)
            : [...completedSteps, stepIdx];
        
        setCompletedSteps(newCompletedSteps);
        
        if (plan) {
            try {
                await updatePlan(plan.id, {
                    completed_steps: newCompletedSteps,
                });
            } catch (error) {
                const msg = error?.response?.data?.detail;
                console.error('Failed to update completed steps:', error);
                if (error?.response?.status === 409 && msg) {
                    window.alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
                }
            }
        }
    };

    const handleMarkImplemented = async () => {
        if (!plan || plan.status === 'superseded') return;
        
        setSaving(true);
        try {
            const newImplementedStatus = !plan.implemented;
            const newStatus = newImplementedStatus ? 'completed' : 'active';
            
            await updatePlan(plan.id, {
                implemented: newImplementedStatus,
                status: newStatus,
                completed_steps: completedSteps,
            });
            
            await fetchPlan();
        } catch (error) {
            const msg = error?.response?.data?.detail;
            console.error('Failed to update plan:', error);
            if (error?.response?.status === 409 && msg) {
                window.alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
            }
        } finally {
            setSaving(false);
        }
    };

    const handleEvaluate = async () => {
        if (!plan || plan.status === 'superseded') return;
        
        setEvaluating(true);
        try {
            await evaluatePlan(plan.id);
            await fetchPlan();
        } catch (error) {
            console.error('Failed to evaluate plan:', error);
            const msg = error?.response?.data?.detail;
            const status = error?.response?.status;
            if (status === 409 && msg) {
                window.alert(typeof msg === 'string' ? msg : JSON.stringify(msg));
            } else {
                alert('Failed to evaluate plan. Please try again.');
            }
        } finally {
            setEvaluating(false);
        }
    };

    const totalSteps = plan?.steps?.length || 0;
    const completedCount = completedSteps.length;
    const progressPercent = totalSteps > 0 ? (completedCount / totalSteps) * 100 : 0;

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <Loader className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-4" />
                    <p className="text-gray-600">{t('page-plan-detail:loadingPlan')}</p>
                </div>
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">{t('page-plan-detail:planNotFound')}</p>
                    <button
                        onClick={() => navigate('/plans')}
                        className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                        {t('page-plan-detail:goBackToPlans')}
                    </button>
                </div>
            </div>
        );
    }

    const priorityStyle = PRIORITY_COLORS[plan.priority] || PRIORITY_COLORS.LOW;
    const isHistoricalSnapshot = plan.status === 'superseded';

    const getDisplayExpectedImpact = () => {
        if (plan.expected_impact && plan.expected_impact !== 'Not specified') {
            return plan.expected_impact;
        }
        if (plan.latest_evaluation?.feedback) {
            const sentences = plan.latest_evaluation.feedback.split('.');
            return sentences.slice(0, 2).join('.') + '.';
        }
        return t('page-plan-detail:notSpecified');
    };

    const getDisplayCurrentValue = () => {
        if (plan.current_value && plan.current_value !== 'Not specified') {
            return plan.current_value;
        }
        if (plan.latest_evaluation?.metrics_comparison?.length > 0) {
            const metrics = plan.latest_evaluation.metrics_comparison;
            const hasChanges = metrics.some(m => m.status === 'improved' || m.status === 'declined');
            if (hasChanges) {
                return t('page-plan-detail:metricsTracked', { count: metrics.length });
            }
            return metrics.map(m => `${m.metric_name}: ${m.baseline_value}`).join(', ');
        }
        return t('page-plan-detail:notSpecified');
    };

    const getDisplayPotentialValue = () => {
        if (plan.potential_value && plan.potential_value !== 'Not specified') {
            return plan.potential_value;
        }
        if (plan.latest_evaluation?.success_score !== undefined) {
            const score = Math.round(plan.latest_evaluation.success_score);
            if (score >= 70) {
                return t('page-plan-detail:highImpactAchieved');
            } else if (score >= 40) {
                return t('page-plan-detail:moderateImpactObserved');
            } else {
                return t('page-plan-detail:impactPendingActionNeeded');
            }
        }
        return t('page-plan-detail:notSpecified');
    };

    return (
        <div className="page-shell">
            {/* Header */}
            <div className="mb-6">
                <button
                    onClick={() => {
                        const db = databaseId || plan?.database_connection_id;
                        if (reportId && timeFrameType && db) {
                            navigate(`/reports/${db}?view=${reportId}&timeFrame=${timeFrameType}`);
                        } else if (plan?.report_generation_id && plan?.time_frame_type && db) {
                            navigate(`/reports/${db}?view=${plan.report_generation_id}&timeFrame=${plan.time_frame_type}`);
                        } else if (db) {
                            navigate(`/reports/${db}`);
                        } else {
                            navigate('/plans');
                        }
                    }}
                    className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('page-plan-detail:backToReport')}
                </button>

                {isHistoricalSnapshot && (
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex gap-3 text-sm text-amber-950">
                            <GitBranch className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold">{t('page-plan-detail:historicalSnapshotTitle')}</p>
                                <p className="text-amber-900/90 mt-1 leading-snug">
                                    {t('page-plan-detail:historicalSnapshotBody')}
                                </p>
                            </div>
                        </div>
                        {plan.continued_by_plan_id ? (
                            <button
                                type="button"
                                onClick={openContinuedPlan}
                                className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold bg-amber-700 text-white hover:bg-amber-800 whitespace-nowrap"
                            >
                                {t('page-plan-detail:openCurrentPlan')}
                            </button>
                        ) : null}
                    </div>
                )}

                {!isHistoricalSnapshot && plan.supersedes_plan_id ? (
                    <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex gap-3 text-sm text-sky-950">
                            <GitBranch className="w-5 h-5 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold">{t('page-plan-detail:continuedFromSyncTitle')}</p>
                                <p className="text-sky-900/90 mt-1 leading-snug">
                                    {t('page-plan-detail:continuedFromSyncBody', { planId: plan.supersedes_plan_id })}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={openPriorPlan}
                            className="inline-flex items-center justify-center px-4 py-2 rounded-lg text-sm font-medium border border-sky-300 bg-white text-sky-900 hover:bg-sky-100 whitespace-nowrap"
                        >
                            {t('page-plan-detail:viewPriorSnapshot')}
                        </button>
                    </div>
                ) : null}
                
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                            <h2 className="text-2xl font-bold text-gray-900">{plan.topic}</h2>
                            <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                                {plan.time_frame_type && (
                                    <span>
                                        {TIME_FRAME_LABEL_KEYS[plan.time_frame_type]
                                            ? t(`page-plan-detail:${TIME_FRAME_LABEL_KEYS[plan.time_frame_type]}`)
                                            : plan.time_frame_type}
                                    </span>
                                )}
                                {plan.owner && (
                                    <span>| {t('page-plan-detail:ownerLabel')}: {plan.owner}</span>
                                )}
                                {plan.created_at && (
                                    <span>| {t('page-plan-detail:createdLabel')}: {formatLocalDate(plan.created_at)}</span>
                                )}
                            </div>
                        </div>
                        {plan.priority && (
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${priorityStyle.bg} ${priorityStyle.text}`}>
                                {plan.priority}
                            </span>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-2">
                        {/* Highlight Chart Section */}
                        {plan.highlight_chart && (
                            <PlanDetailSectionCard
                                title={t('page-plan-detail:sectionTitles.highlight')}
                                padding="md"
                                className="rounded-xl mb-6 overflow-hidden"
                            >
                                <HtmlRenderer htmlContent={plan.highlight_chart} />
                            </PlanDetailSectionCard>
                        )}

                        <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('page-plan-detail:planDetails')}</h2>

                        <Card padding="md" className="rounded-xl mb-6">
                            <div className="mb-4">
                                <div className="text-sm font-medium text-gray-500 mb-2">{t('page-plan-detail:expectedImpact')}</div>
                                <div className="text-gray-900">{getDisplayExpectedImpact()}</div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <div>
                                    <div className="text-sm font-medium text-gray-500 mb-1">{t('page-plan-detail:currentValue')}</div>
                                    <div className="text-gray-900">{getDisplayCurrentValue()}</div>
                                </div>
                                <div>
                                    <div className="text-sm font-medium text-gray-500 mb-1">{t('page-plan-detail:potentialValue')}</div>
                                    <div className="text-gray-900">{getDisplayPotentialValue()}</div>
                                </div>
                            </div>

                            {plan.latest_evaluation?.success_score !== undefined && (
                                <div className="pt-4 border-t border-gray-100">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-500">{t('page-plan-detail:successScore')}</span>
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold ${
                                            plan.latest_evaluation.success_score >= 70
                                                ? 'bg-green-100 text-green-800'
                                                : plan.latest_evaluation.success_score >= 40
                                                ? 'bg-yellow-100 text-yellow-800'
                                                : 'bg-red-100 text-red-800'
                                        }`}>
                                            {Math.round(plan.latest_evaluation.success_score)}%
                                        </span>
                                    </div>
                                </div>
                            )}
                        </Card>

                        {plan.frontend_code && (
                            <PlanDetailSectionCard
                                title={t('page-plan-detail:sectionTitles.recommendation')}
                                padding="none"
                                className="mb-6 rounded-xl overflow-hidden"
                            >
                                <HtmlRenderer
                                    htmlContent={stripFrontendCodeOuterCard(plan.frontend_code)}
                                    minHeight={120}
                                    bodyPaddingPx={24}
                                    className="w-full border-none block"
                                />
                            </PlanDetailSectionCard>
                        )}

                        {plan.latest_evaluation && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('page-plan-detail:latestEvaluation')}</h3>
                                <Card padding="md" className="rounded-xl">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-2">
                                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${
                                                plan.latest_evaluation.success_score >= 70
                                                    ? 'bg-green-100 text-green-800'
                                                    : plan.latest_evaluation.success_score >= 40
                                                    ? 'bg-yellow-100 text-yellow-800'
                                                    : 'bg-red-100 text-red-800'
                                            }`}>
                                                {t('page-plan-detail:successPercent', { score: Math.round(plan.latest_evaluation.success_score) })}
                                            </span>
                                        </div>
                                        <span className="text-sm text-gray-500">
                                            {formatLocalDate(plan.latest_evaluation.evaluated_at)}
                                        </span>
                                    </div>
                                    
                                    {plan.latest_evaluation.metrics_comparison?.length > 0 && (
                                        <div className="mb-4">
                                            <div className="text-sm font-medium text-gray-500 mb-2">{t('page-plan-detail:metricsComparison')}</div>
                                            <div className="space-y-2">
                                                {plan.latest_evaluation.metrics_comparison.map((metric, idx) => (
                                                    <div key={idx} className="flex items-center justify-between text-sm">
                                                        <span className="text-gray-700">{metric.metric_name}</span>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-gray-400">{metric.baseline_value}</span>
                                                            <span className="text-gray-300">→</span>
                                                            <span className="font-medium text-gray-900">{metric.current_value}</span>
                                                            <span className={`text-xs font-medium ${
                                                                metric.status === 'improved' ? 'text-green-600' :
                                                                metric.status === 'declined' ? 'text-red-600' :
                                                                'text-gray-600'
                                                            }`}>
                                                                {metric.change_percent}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    
                                    {plan.latest_evaluation.feedback && (
                                        <div>
                                            <div className="text-sm font-medium text-gray-500 mb-2">{t('page-plan-detail:feedback')}</div>
                                            <div className="text-gray-700 text-sm prose prose-sm max-w-none">
                                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                    {plan.latest_evaluation.feedback}
                                                </ReactMarkdown>
                                            </div>
                                        </div>
                                    )}

                                    {plan.evaluation_history?.length > 1 && (
                                        <button
                                            onClick={() => setShowEvaluationHistory(!showEvaluationHistory)}
                                            className="mt-4 flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                                        >
                                            {showEvaluationHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                            {t('page-plan-detail:viewAllEvaluations', { count: plan.evaluation_history.length })}
                                        </button>
                                    )}

                                    {showEvaluationHistory && plan.evaluation_history?.length > 1 && (
                                        <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                                            {plan.evaluation_history.slice(0, -1).reverse().map((evalItem, idx) => (
                                                <div key={idx} className="p-3 bg-gray-50 rounded-lg text-sm">
                                                    <div className="flex items-center justify-between mb-2">
                                                        <span className="font-medium">{Math.round(evalItem.success_score)}%</span>
                                                        <span className="text-gray-500">{formatLocalDate(evalItem.evaluated_at)}</span>
                                                    </div>
                                                    {evalItem.feedback && (
                                                        <div className="text-gray-600 prose prose-sm max-w-none">
                                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                                {evalItem.feedback}
                                                            </ReactMarkdown>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </Card>
                            </div>
                        )}
                    </div>

                    <div>
                        <PlanDetailSectionCard
                            title={t('page-plan-detail:sectionTitles.implementationSteps')}
                            padding="md"
                            className="rounded-xl mb-6"
                        >
                            <div className="flex items-center justify-between mb-4">
                                <span className="text-sm font-medium text-gray-700">
                                    {t('page-plan-detail:progressLabel', { completed: completedCount, total: totalSteps })}
                                </span>
                                <span className="text-sm font-semibold text-primary-600">
                                    {Math.round(progressPercent)}%
                                </span>
                            </div>
                            
                            <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
                                <div
                                    className={`h-2 rounded-full transition-all duration-300 ${
                                        plan.implemented ? 'bg-green-600' : 'bg-primary-600'
                                    }`}
                                    style={{ width: `${progressPercent}%` }}
                                ></div>
                            </div>

                            <div className="space-y-4">
                                {plan.steps?.map((step, idx) => {
                                    const isCompleted = completedSteps.includes(idx);
                                    return (
                                        <div
                                            key={idx}
                                            className={`flex gap-3 p-3 rounded-lg transition-colors ${
                                                isCompleted ? 'bg-green-50' : 'bg-gray-50'
                                            }`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() => !plan.implemented && !isHistoricalSnapshot && toggleStep(idx)}
                                                disabled={plan.implemented || isHistoricalSnapshot}
                                                className={`plan-step-toggle flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                                                    isCompleted
                                                        ? 'bg-green-600 border-green-600 text-white'
                                                        : plan.implemented || isHistoricalSnapshot
                                                        ? 'border-gray-300 text-gray-300 cursor-not-allowed'
                                                        : 'border-gray-300 hover:border-primary-600 text-transparent hover:text-primary-600 cursor-pointer'
                                                }`}
                                            >
                                                {isCompleted && <CheckCircle className="w-4 h-4" />}
                                                {!isCompleted && !plan.implemented && !isHistoricalSnapshot && <span className="text-xs font-semibold">{idx + 1}</span>}
                                                {!isCompleted && (plan.implemented || isHistoricalSnapshot) && <span className="text-xs font-semibold">{idx + 1}</span>}
                                            </button>
                                            <p className={`flex-1 text-sm leading-relaxed ${
                                                isCompleted ? 'text-gray-500 line-through' : 'text-gray-700'
                                            }`}>
                                                {step}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="mt-6 pt-6 border-t border-gray-200 flex flex-wrap gap-3">
                                <button
                                    onClick={handleMarkImplemented}
                                    disabled={isHistoricalSnapshot || saving || (completedCount !== totalSteps && !plan.implemented)}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                        plan.implemented
                                            ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                            : completedCount === totalSteps
                                            ? 'bg-green-600 text-white hover:bg-green-700'
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    {saving ? (
                                        <span className="flex items-center gap-2">
                                            <Loader className="w-4 h-4 animate-spin" />
                                            {t('common:saving')}
                                        </span>
                                    ) : plan.implemented ? (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4" />
                                            {t('page-plan-detail:markAsNotImplemented')}
                                        </span>
                                    ) : (
                                        <span className="flex items-center gap-2">
                                            <CheckCircle className="w-4 h-4" />
                                            {t('page-plan-detail:markAsImplemented')}
                                        </span>
                                    )}
                                </button>

                                {plan.implemented && (
                                    <button
                                        onClick={handleEvaluate}
                                        disabled={isHistoricalSnapshot || evaluating}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                                            evaluating
                                                ? 'bg-purple-300 text-white cursor-not-allowed'
                                                : 'bg-purple-600 text-white hover:bg-purple-700'
                                        }`}
                                    >
                                        {evaluating ? (
                                            <span className="flex items-center gap-2">
                                                <Loader className="w-4 h-4 animate-spin" />
                                                {t('page-plan-detail:evaluating')}
                                            </span>
                                        ) : (
                                            <span className="flex items-center gap-2">
                                                <PlayCircle className="w-4 h-4" />
                                                {plan.latest_evaluation ? t('page-plan-detail:reEvaluateImpact') : t('page-plan-detail:evaluateImpact')}
                                            </span>
                                        )}
                                    </button>
                                )}
                            </div>
                        </PlanDetailSectionCard>

                        {plan.skills?.length > 0 && (
                            <PlanDetailSectionCard
                                title={t('page-plan-detail:sectionTitles.skills')}
                                padding="md"
                                className="rounded-xl mb-6"
                            >
                                <p className="text-xs text-gray-500 mb-3">
                                    <Trans
                                        i18nKey="page-plan-detail:skillsDescription"
                                        components={{
                                            strong: <strong />,
                                            code1: <code className="bg-gray-100 px-1 rounded" />,
                                            code2: <code className="bg-gray-100 px-1 rounded" />,
                                        }}
                                    />
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {plan.skills.map((s) => (
                                        <span
                                            key={s}
                                            className="inline-flex px-2.5 py-1 rounded-lg text-xs font-mono bg-primary-50 text-primary-900 border border-primary-100 break-all max-w-full"
                                        >
                                            {s}
                                        </span>
                                    ))}
                                </div>
                            </PlanDetailSectionCard>
                        )}

                        {plan.baseline_query_ids?.length > 0 && (
                            <PlanDetailSectionCard
                                title={t('page-plan-detail:sectionTitles.baseline')}
                                padding="md"
                                className="rounded-xl"
                            >
                                <div className="space-y-3 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('page-plan-detail:baselineQueries')}</span>
                                        <span className="font-medium">{plan.baseline_query_ids.length}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-500">{t('page-plan-detail:lastEvaluated')}</span>
                                        <span className="font-medium">
                                            {plan.evaluated_at ? formatLocalDate(plan.evaluated_at) : t('page-plan-detail:never')}
                                        </span>
                                    </div>

                                    {plan.baseline_parameters && plan.baseline_parameters.length > 0 && (
                                        <div className="mt-4 pt-4 border-t border-gray-100">
                                            <h4 className="font-medium text-gray-700 mb-2">{t('page-plan-detail:queryParameters')}</h4>
                                            <div className="space-y-2">
                                                {plan.baseline_parameters.map((params, idx) => (
                                                    <div key={idx} className="bg-gray-50 rounded p-3 text-xs">
                                                        <div className="font-semibold text-gray-600 mb-1">
                                                            {(() => {
                                                                const qid = plan.baseline_query_ids?.[idx];
                                                                if (!qid) return t('page-plan-detail:queryFallback', { num: idx + 1 });
                                                                return databaseQueriesMap[qid]?.name || t('page-plan-detail:queryFallback', { num: qid });
                                                            })()}
                                                        </div>
                                                        {Object.keys(params).length > 0 ? (
                                                            <div className="space-y-1">
                                                                {Object.entries(params).map(([key, value]) => (
                                                                    <div key={key} className="flex gap-2">
                                                                        <span className="text-gray-500">{key}:</span>
                                                                        <span className="text-gray-900 font-medium">
                                                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                        </span>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="text-gray-400">{t('page-plan-detail:noParameters')}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </PlanDetailSectionCard>
                        )}
                    </div>
                </div>
        </div>
    );
};
